import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOutlet } from '../contexts/OutletContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Ban,
  DollarSign,
  User,
  Calendar,
  RefreshCw,
  Eye,
  Loader2,
  AlertTriangle,
  FileText,
  TrendingDown,
} from 'lucide-react';

export default function VoidLogs() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { currentOutlet } = useOutlet();
  const { formatCurrency } = useCurrency();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    fetchLogs();
    fetchSummary();
  }, [currentOutlet?.id]);

  const fetchLogs = async () => {
    if (!currentOutlet?.id) return;
    
    setLoading(true);
    try {
      const params = { outlet_id: currentOutlet.id };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const resp = await api.get('/void-logs', { params });
      setLogs(resp.data);
    } catch (error) {
      toast.error(t('messages.error.failed_fetch_void_logs'));
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    if (!currentOutlet?.id) return;
    
    try {
      const params = { outlet_id: currentOutlet.id };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const resp = await api.get('/void-logs/summary', { params });
      setSummary(resp.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const handleFilter = () => {
    fetchLogs();
    fetchSummary();
  };

  const viewDetail = (log) => {
    setSelectedLog(log);
    setDetailOpen(true);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 space-y-6" data-testid="void-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Ban className="h-6 w-6 text-red-500" />
            {t('void.title')}
          </h1>
          <p className="text-muted-foreground">{t('void.subtitle')}</p>
        </div>
        <Button variant="outline" onClick={handleFilter} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {t('common.refresh')}
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Ban className="h-4 w-4" />
                {t('void.total_voids')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-500">{summary.total_voids}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                {t('void.total_amount_voided')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-500">{formatCurrency(summary.total_amount)}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <TrendingDown className="h-4 w-4" />
                {t('void.cash_voids')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{formatCurrency(summary.cash_voids_amount)}</p>
              <p className="text-xs text-muted-foreground">{t('void.affects_reconciliation')}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{t('void.paid_vs_unpaid')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Badge variant="destructive">{summary.paid_voids} {t('void.paid')}</Badge>
                <Badge variant="secondary">{summary.unpaid_voids} {t('void.unpaid')}</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* By User Summary */}
      {summary?.by_user && Object.keys(summary.by_user).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{t('void.voids_by_user')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {Object.entries(summary.by_user).map(([name, data]) => (
                <div key={name} className="bg-muted rounded-lg p-3">
                  <p className="font-medium">{name}</p>
                  <p className="text-sm text-muted-foreground">
                    {data.count} {t('void.voids')} • {formatCurrency(data.amount)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-4 items-end">
            <div>
              <Label>{t('void.from')}</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-40"
              />
            </div>
            <div>
              <Label>{t('void.to')}</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-40"
              />
            </div>
            <Button onClick={handleFilter}>{t('void.filter')}</Button>
            <Button variant="outline" onClick={() => { setDateFrom(''); setDateTo(''); }}>{t('void.clear')}</Button>
          </div>
        </CardContent>
      </Card>

      {/* Void Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('void.void_history')}</CardTitle>
          <CardDescription>{t('void.void_history_desc')}</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">{t('void.no_void_logs')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('void.table_headers.date_time')}</TableHead>
                  <TableHead>{t('void.table_headers.order')}</TableHead>
                  <TableHead>{t('void.table_headers.amount')}</TableHead>
                  <TableHead>{t('void.table_headers.was_paid')}</TableHead>
                  <TableHead>{t('void.table_headers.voided_by')}</TableHead>
                  <TableHead>{t('void.table_headers.reason')}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-sm">
                      {formatDateTime(log.created_at)}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono font-medium">{log.order_number}</span>
                      {log.table_number && (
                        <span className="text-xs text-muted-foreground ml-2">
                          {t('void.table')} {log.table_number}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono font-medium text-red-500">
                      {formatCurrency(log.amount)}
                    </TableCell>
                    <TableCell>
                      {log.was_paid ? (
                        <Badge variant="destructive" className="text-xs">
                          {t('void.paid')} • {log.payment_method}
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">{t('void.unpaid')}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{log.voided_by_name}</p>
                          <p className="text-xs text-muted-foreground">{log.voided_by_role}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {log.reason}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => viewDetail(log)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {t('void.detail')}
            </DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                <p className="text-sm text-red-500">{t('void.order_voided')}</p>
                <p className="text-2xl font-bold font-mono">{selectedLog.order_number}</p>
                <p className="text-xl font-bold text-red-500 mt-2">
                  {formatCurrency(selectedLog.amount)}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-muted-foreground">{t('void.table_headers.date_time')}</Label>
                  <p className="font-medium">{formatDateTime(selectedLog.created_at)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('void.order_type')}</Label>
                  <p className="font-medium">{selectedLog.order_type}</p>
                </div>
                {selectedLog.table_number && (
                  <div>
                    <Label className="text-muted-foreground">{t('void.table')}</Label>
                    <p className="font-medium">{selectedLog.table_number}</p>
                  </div>
                )}
                <div>
                  <Label className="text-muted-foreground">{t('void.items')}</Label>
                  <p className="font-medium">{selectedLog.items_count} {t('void.items')}</p>
                </div>
              </div>

              <div className="border rounded-lg p-4 space-y-2">
                <Label className="text-muted-foreground">{t('void.table_headers.voided_by')}</Label>
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{selectedLog.voided_by_name}</p>
                    <p className="text-sm text-muted-foreground">{selectedLog.voided_by_role}</p>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-4">
                <Label className="text-muted-foreground">{t('void.payment_status')}</Label>
                <div className="mt-2">
                  {selectedLog.was_paid ? (
                    <div className="flex items-center gap-2">
                      <Badge variant="destructive">{t('void.paid')}</Badge>
                      <span className="text-sm">{t('void.via')} {selectedLog.payment_method}</span>
                    </div>
                  ) : (
                    <Badge variant="secondary">{t('void.unpaid')}</Badge>
                  )}
                </div>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <Label className="text-yellow-500 flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" />
                  {t('void.void_reason')}
                </Label>
                <p className="mt-2 font-medium">{selectedLog.reason}</p>
              </div>

              {selectedLog.shift_id && (
                <div className="text-xs text-muted-foreground">
                  {t('void.shift_id')}: {selectedLog.shift_id}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
